"use client";

import React, { useState, useMemo } from "react";
import Badge from "@ishvaram/ui/Badge";
import Button from "@ishvaram/ui/Button";
import Card from "@ishvaram/ui/Card";
import Typography from "@ishvaram/ui/Typography";
import { SacredImage } from "@ishvaram/ui/SacredImage";
import { AnimatedView } from "@ishvaram/ui/AnimatedView";
import { Collapsible } from "@ishvaram/ui/Collapsible";
import type {
  ChartRemedyResponse,
  RemedyDoshaInfo,
  RemedyBundle,
  EnrichedProduct,
} from "@/lib/api/remedies";
import { withShopifyAttribution } from "@/lib/shopify-attribution";

const TYPE_LABELS: Record<string, string> = {
  gemstone: "Sacred Gemstone",
  rudraksha: "Authentic Rudraksha",
  puja: "Vedic Puja",
  yantra: "Energized Yantra",
  mala: "Blessed Japa Mala",
};

function DoshaSelectionCard({
  dosha,
  isSelected,
  onSelect,
}: {
  dosha: RemedyDoshaInfo;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const severityLevel =
    dosha.severity >= 0.8 ? "Major" : dosha.severity >= 0.5 ? "Significant" : "Mild";
  const severityColor =
    dosha.severity >= 0.8
      ? "text-error"
      : dosha.severity >= 0.5
        ? "text-warning"
        : "text-ish-accent";

  return (
    <div
      onClick={onSelect}
      className={`cursor-pointer transition-all ${isSelected ? "scale-[1.02]" : "hover:scale-[1.01]"}`}
    >
      <Card
        className={`p-4 h-full border-1.5 ${isSelected ? "border-ish-accent bg-ish-accent-light/30 shadow-md" : "border-ish-border bg-white shadow-sm"}`}
      >
        <div className="flex flex-col gap-2">
          <Badge
            label={`${severityLevel} ${dosha.name_en}`}
            variant={dosha.severity >= 0.8 ? "error" : "warning"}
          />
          <Typography variant="subtitle" className="text-[15px] font-bold text-ish-dark">
            {dosha.name_hi}
          </Typography>
          <Typography variant="body" className="text-[12px] text-ish-muted line-clamp-2">
            {dosha.description}
          </Typography>
          <div className="mt-auto pt-2 flex items-center justify-between">
            <span className={`text-[11px] font-bold uppercase tracking-wider ${severityColor}`}>
              Severity: {Math.round(dosha.severity * 100)}%
            </span>
            <div
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isSelected ? "border-ish-accent" : "border-ish-border"}`}
            >
              {isSelected && <div className="w-2 h-2 rounded-full bg-ish-accent" />}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function RemedyBundleCard({
  bundle,
  isBestValue,
}: {
  bundle: RemedyBundle;
  isBestValue?: boolean;
}) {
  return (
    <Card
      className={`relative p-6 flex flex-col h-full border-1.5 ${isBestValue ? "border-ish-accent bg-linear-to-br from-ish-accent-light/30 to-white shadow-lg" : "border-ish-border bg-white shadow-md"}`}
    >
      {isBestValue && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-ish-accent text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.1em] shadow-sm">
          Jyotishi Recommendation
        </div>
      )}

      <div className="mb-4">
        <Typography variant="title" className="text-[17px] font-bold mb-1 text-ish-dark font-serif">
          {bundle.name}
        </Typography>
        <Typography variant="body" className="text-[13px] text-ish-muted leading-relaxed">
          {bundle.description}
        </Typography>
      </div>

      <div className="space-y-3 mb-6 flex-1">
        {bundle.products.map((p, idx) => (
          <div
            key={idx}
            className="flex gap-3 items-start p-2 rounded-lg bg-white/50 border border-ish-border/30"
          >
            <SacredImage
              src={p.image_url || ""}
              alt={p.product_name}
              className="w-10 h-10 rounded shadow-sm shrink-0"
            />
            <div className="min-w-0">
              <Typography
                variant="body"
                className="text-[13px] font-semibold text-ish-dark truncate"
              >
                {p.product_name}
              </Typography>
              <Typography variant="body" className="text-[11px] text-ish-faint">
                {(p.type ? TYPE_LABELS[p.type] : null) || p.type || "Remedy"}
              </Typography>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-auto pt-4 border-t border-ish-border/50">
        <div className="flex items-center justify-between mb-4">
          <div>
            <Typography variant="body" className="text-[12px] text-ish-faint line-through">
              ₹{bundle.individual_total}
            </Typography>
            <Typography variant="title" className="text-[20px] font-bold text-ish-dark">
              ₹{bundle.bundle_price}
            </Typography>
          </div>
          {bundle.discount_percent > 0 && (
            <Badge label={`Save ${bundle.discount_percent}%`} variant="success" />
          )}
        </div>

        <Button
          title="Add to Cart →"
          variant={isBestValue ? "primary" : "secondary"}
          onPress={() => {
            if (bundle.checkout_url) {
              window.open(bundle.checkout_url, "_blank");
            }
          }}
        />
      </div>
    </Card>
  );
}

function ProductRecommendationCard({ product }: { product: EnrichedProduct }) {
  return (
    <Card className="p-4 flex gap-4 bg-white border-ish-border shadow-sm hover:shadow-md transition-shadow group">
      <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0 border border-ish-border/50">
        <SacredImage
          src={product.image_url || ""}
          alt={product.product_name}
          className="w-full h-full object-cover transition-transform group-hover:scale-110"
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col">
        <Typography variant="body" className="text-[14px] font-bold text-ish-dark truncate">
          {product.product_name}
        </Typography>
        <Typography
          variant="body"
          className="text-[12px] text-ish-muted line-clamp-2 mt-0.5 leading-relaxed"
        >
          {product.reason}
        </Typography>
        <div className="mt-auto flex items-center justify-between pt-2">
          <Typography variant="body" className="text-[14px] font-bold text-ish-accent">
            ₹{product.current_price || "..."}
          </Typography>
          <Button
            title="View"
            variant="secondary"
            onPress={() => {
              if (product.shopify_url) {
                window.open(
                  withShopifyAttribution(product.shopify_url, window.location.pathname, {
                    offer: "product",
                    placement: "remedies_product_card",
                  }),
                  "_blank"
                );
              }
            }}
          />
        </div>
      </div>
    </Card>
  );
}

export default function Remedies({ result }: { result: ChartRemedyResponse }) {
  const [selectedDoshaId, setSelectedDoshaId] = useState<string>(
    result.doshas.length > 0 ? result.doshas[0].dosha_id : ""
  );

  const selectedDoshaData = useMemo(
    () => result.doshas.find((d) => d.dosha_id === selectedDoshaId),
    [result, selectedDoshaId]
  );

  return (
    <div className="sacred-bg min-h-screen">
      <div className="mx-auto max-w-4xl px-4 py-12 sm:py-20">
        <div className="text-center mb-12 animate-slide-up">
          <Badge label="Rituals & Remedies" variant="accent" className="mb-4" />
          <Typography
            variant="headline"
            className="text-[28px] sm:text-[36px] font-bold leading-tight text-ish-dark font-serif"
          >
            Personalized Vedic Remedies
          </Typography>
          <Typography
            variant="body"
            className="mt-4 text-[16px] text-ish-muted max-w-2xl mx-auto leading-relaxed"
          >
            Based on your birth chart, our AI Jyotishi has identified specific planetary afflictions
            and prescribed authentic remedies to harmonize your cosmic energy.
          </Typography>
        </div>

        {result.doshas.length > 0 && (
          <div className="mb-12">
            <Typography
              variant="title"
              className="text-[18px] font-bold mb-5 text-ish-dark font-serif"
            >
              Step 1: Select a Dosha to Address
            </Typography>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {result.doshas.map((dosha) => (
                <DoshaSelectionCard
                  key={dosha.dosha_id}
                  dosha={dosha}
                  isSelected={selectedDoshaId === dosha.dosha_id}
                  onSelect={() => setSelectedDoshaId(dosha.dosha_id)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 space-y-6">
          {selectedDoshaData && (
            <AnimatedView>
              <Card className="p-6 border-ish-accent bg-ish-accent-light/10 mb-8 border-l-4">
                <Typography
                  variant="headline"
                  className="text-[20px] font-bold mb-4 text-ish-dark font-serif"
                >
                  Analysis: {selectedDoshaData.name_en}
                </Typography>
                <div className="prose prose-stone max-w-none text-[15px] leading-relaxed text-ish-muted">
                  {selectedDoshaData.description}
                </div>
              </Card>

              {result.bundles &&
                result.bundles.filter((b) => b.primary_dosha_addressed === selectedDoshaId).length >
                  0 && (
                  <div className="mb-12">
                    <Typography
                      variant="title"
                      className="text-[18px] font-bold mb-6 text-ish-dark font-serif"
                    >
                      Recommended Remedy Bundles
                    </Typography>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
                      {result.bundles
                        .filter((b) => b.primary_dosha_addressed === selectedDoshaId)
                        .map((bundle, idx) => (
                          <RemedyBundleCard key={idx} bundle={bundle} isBestValue={idx === 0} />
                        ))}
                    </div>
                  </div>
                )}

              {selectedDoshaData.individual_recommendations &&
                selectedDoshaData.individual_recommendations.length > 0 && (
                  <div>
                    <Typography
                      variant="headline"
                      className="text-[20px] font-bold mb-6 text-ish-dark font-serif"
                    >
                      Individual Recommendations
                    </Typography>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {selectedDoshaData.individual_recommendations.map((product, idx) => (
                        <ProductRecommendationCard key={idx} product={product} />
                      ))}
                    </div>
                  </div>
                )}
            </AnimatedView>
          )}
        </div>

        <div className="mt-16 pt-12 border-t border-ish-border/50">
          <Card className="p-8 text-center bg-ish-dark text-white rounded-3xl overflow-hidden relative">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://ishvaram.com/sacred-pattern.png')] bg-repeat" />
            <div className="relative z-10">
              <Typography
                variant="headline"
                className="text-[24px] font-bold mb-3 text-white font-serif"
              >
                Need Deeper Guidance?
              </Typography>
              <Typography
                variant="body"
                className="text-[15px] text-white/70 mb-8 max-w-lg mx-auto"
              >
                Consult with our AI Jyotishi for a comprehensive analysis of your birth chart
                including career, relationship, and health forecasts.
              </Typography>
              <div className="flex justify-center">
                <Button
                  title="Free Kundali Analysis →"
                  variant="primary"
                  onPress={() => {
                    window.location.href = "/free-kundali";
                  }}
                />
              </div>
            </div>
          </Card>
        </div>

        <div className="mt-12">
          <Collapsible title="Why follow these remedies?" defaultOpen={false}>
            <div className="p-4 bg-white/50 rounded-lg">
              <Typography variant="body" className="text-[14px] leading-relaxed text-ish-muted">
                In Vedic tradition, planets are energies that influence our karma. When a planet is
                &quot;afflicted&quot; (due to its position or associations), it can create
                obstacles. Remedies act as a filter or balancer—gemstones use color therapy, mantras
                use sound frequency, and pujas align intention to harmonize these cosmic forces.
              </Typography>
            </div>
          </Collapsible>
        </div>
      </div>
    </div>
  );
}
